import { z } from 'zod/v4';
export const CaseCategorySchema = z.enum([
    'appellate',
    'civil',
    'civil-limited',
    'civil-restraining-order',
    'civil-unlimited',
    'criminal',
    'family',
    'family-dcss',
    'juvenile',
    'juvenile-dependency',
    'mental-health',
    'probate',
    'probate-mental-health',
    'small-claims',
    'traffic',
]);
export const CaseStatusSchema = z.enum([
    'active',
    'inactive',
    'closed',
]);
// EFM case type (dynamic, loaded from EFM per category per county)
export const CaseTypeRefSchema = z.object({
    code: z.string(),
    name: z.string(),
});
// EFM case sub-type (only used for Appellate in LA County)
export const CaseSubTypeRefSchema = z.object({
    code: z.string(),
    name: z.string(),
});
// Court jurisdiction (which court has jurisdiction — Family, etc.)
export const CourtJurisdictionRefSchema = z.object({
    code: z.string(),
    name: z.string(),
});
// Jurisdictional amount — accepts JTI policy codes (dynamic) or legacy enum values
export const JurisdictionalAmountSchema = z.string().min(1);
// Additional case information flags
export const AdditionalCaseInfoSchema = z.object({
    conditionallyUnderSeal: z.boolean().default(false),
    confidentialCase: z.boolean().default(false),
    complexCase: z.boolean().default(false),
    classActionIndicator: z.boolean().default(false),
});
// Premises address for case info
export const PremisesAddressSchema = z.object({
    address1: z.string().optional(),
    address2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zip: z.string().optional(),
});
// Case type metadata (form-control flags from EFM)
export const CaseTypeMetadataSchema = z.object({
    isIncidentZipAvailable: z.boolean().default(true),
    showCasePlugin: z.boolean().default(true),
    showCaseStreetAddress: z.boolean().default(false),
    isCaseStreetAddressRequired: z.boolean().default(false),
    showJurisdictionalAmount: z.boolean().default(false),
    showCourtJurisdiction: z.boolean().default(false),
});
// Legacy party types (kept for backward compat with existing orders)
export const PartyTypeSchema = z.enum([
    'individual',
    'business',
    'attorney',
    'government',
]);
// eFiling party type (person vs organization)
export const EFilingPartyTypeSchema = z.enum([
    'person',
    'organization',
]);
// Organization type for eFiling
export const OrganizationTypeSchema = z.enum([
    'ENTITY_OTHER',
    'GOVERNMENT_AGENCY',
    'TRUST',
]);
// Party sub-type flags (person only)
export const PartySubTypeSchema = z.enum([
    'guardian-ad-litem',
    'incompetent-person',
    'minor',
]);
// Alternate name types
export const AlternateNameTypeSchema = z.enum([
    'AKA',
    'Alias',
    'DBA',
    'ESA',
    'FDBA',
    'FKA',
]);
export const AlternateNameSchema = z.object({
    type: AlternateNameTypeSchema,
    name: z.string(),
});
// Name suffix
export const NameSuffixSchema = z.enum([
    'Jr.',
    'Sr.',
    'II',
    'III',
    'IV',
]);
// Self-represented party address
export const SelfRepAddressSchema = z.object({
    country: z.string().default('US'),
    address1: z.string().optional(),
    streetSuffix: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zip: z.string().optional(),
});
// Court-returned participant fee-waiver fields (SOR-239), mirroring the
// case-lookup parser's CasePartyCourtWaiver field-for-field — the same object
// crosses the wire from GetCase onto the wizard party. Declared on
// EFilingPartySchema so the value survives the type_data Zod round-trip
// (an undeclared key would be silently stripped on every parse).
export const CourtWaiverSchema = z.object({
    /** An explicit court "no" is data — carried as false, never coerced. */
    efspFeeWaiverFiled: z.boolean().optional(),
    efmExemptionType: z.string().optional(),
    efmFeeExemptionType: z.string().optional(),
});
// Per-entry source attribution for wizard parties/attorneys (SOR-223). An entry
// is minted by exactly one of: an applied OCR extraction patch, case-attach
// roster hydration, or manual user entry. Optional so existing persisted drafts
// and server reads parse unchanged — readers treat an absent tag as 'manual'
// defensively (loadDraft backfills it; mergeExtractionRoster's withDefaultSource
// is the deterministic default).
export const EntrySourceSchema = z.enum(['extraction', 'case-roster', 'manual']);
// eFiling Party schema (expanded for wizard)
export const EFilingPartySchema = z.object({
    id: z.string().uuid(),
    /** Court-assigned party ID from GetCase (subsequent filing) */
    externalId: z.string().optional(),
    /** Source attribution (SOR-223): which mint chokepoint produced this entry. */
    source: EntrySourceSchema.optional(),
    partyType: EFilingPartyTypeSchema,
    // Role
    roleCode: z.string(),
    roleName: z.string(),
    // Person fields
    firstName: z.string().optional(),
    middleName: z.string().optional(),
    lastName: z.string().optional(),
    suffix: NameSuffixSchema.optional(),
    // Organization fields
    organizationName: z.string().optional(),
    organizationType: OrganizationTypeSchema.optional(),
    // Sub-types (person only)
    partySubTypes: z.array(PartySubTypeSchema).default([]),
    // Billing
    isLeadClient: z.boolean().default(false),
    billingZip: z.string().optional(),
    /**
     * True when this party is being added to an existing case via subsequent
     * filing (e.g., amended complaint adding a new plaintiff/defendant). Drives
     * the envelope emission of NEW_PLAINTIFF / NEW_RESPONDING_PARTY metadata
     * items per JTI document-metadata-model. Defaults false (existing party).
     */
    isNewToCase: z.boolean().default(false),
    // Fee-related
    feeExemptionType: z.enum(['NONE', 'FEE_WAIVER', 'GOVT_ENTITY']).default('NONE'),
    requestFeesExemption: z.boolean().default(false),
    feeWaiverOnFile: z.boolean().default(false),
    /**
     * Court-REPORTED waiver status for this party (SOR-239) — read-only
     * observation carried from the case-lookup roster, shown beside (never
     * merged into) the filer's own `feeWaiverOnFile` flag above. Optional with
     * NO default: absent means the court said nothing, and no default party
     * object spells the key.
     */
    courtWaiver: CourtWaiverSchema.optional(),
    // eService consent
    eServiceConsent: z.boolean().default(false),
    // Self-representation
    isSelfRepresented: z.boolean().default(false),
    selfRepAddress: SelfRepAddressSchema.optional(),
    selfRepPhone: z.string().optional(),
    selfRepEmail: z.union([z.string().email(), z.literal('')]).optional(),
    /**
     * General mailing address on the party record, independent of
     * self-representation (Walter R103 — an unlawful-detainer filing must capture
     * the defendant's mailing address; LegalConnect parity). Captured only — not
     * emitted into the EFM envelope; the envelope's contact block still keys off
     * selfRepAddress.
     */
    mailingAddress: SelfRepAddressSchema.optional(),
    // Alternate names
    hasAlternateNames: z.boolean().default(false),
    alternateNames: z.array(AlternateNameSchema).default([]),
    // Interpreter
    requiresInterpreter: z.boolean().default(false),
    interpreterLanguage: z.string().optional(),
});
// Legacy PartySchema (kept for backward compat)
export const PartySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    type: PartyTypeSchema,
    role: z.string(),
    email: z.string().email().optional(),
    phone: z.string().optional(),
    address: z.object({
        street: z.string(),
        city: z.string(),
        state: z.string(),
        zip: z.string(),
    }).optional(),
    attorney: z.object({
        name: z.string(),
        barNumber: z.string().optional(),
        firm: z.string().optional(),
        email: z.string().email().optional(),
        phone: z.string().optional(),
    }).optional(),
});
// Attorney schema for eFiling
export const EFilingAttorneySchema = z.object({
    id: z.string().uuid(),
    /** Court-assigned ID from GetCase (used in subsequent filing metadata) */
    externalId: z.string().optional(),
    /** Source attribution (SOR-223): which mint chokepoint produced this entry. */
    source: EntrySourceSchema.optional(),
    firstName: z.string(),
    middleName: z.string().optional(),
    lastName: z.string(),
    suffix: NameSuffixSchema.optional(),
    barState: z.string(),
    barNumber: z.string(),
    firmName: z.string().optional(),
    email: z.string().email().optional(),
    phone: z.string().optional(),
    address: SelfRepAddressSchema.optional(),
    /** Party IDs this attorney represents (REPRESENTEDBY associations) */
    representedPartyIds: z.array(z.string()).default([]),
    /**
     * WOL-344: Lifecycle role label — display hint for the wizard attorney list.
     * Used by the UI to colour-code and label attorneys without relying on opaque
     * metadata-slot assignment. NOT read by the envelope builder — emission is
     * still driven by mv.FORMER_ATTORNEY and the presence/absence of externalId.
     *
     * 'filing'      — attorney loaded from GetCase; already on record at the court
     * 'substituting' — incoming new attorney in a Substitution-of-Attorney filing
     * 'associating'  — incoming new attorney in an Association-of-Attorney filing
     * 'former'       — outgoing attorney being replaced (designated by the user in
     *                  the FORMER_ATTORNEY metadata dropdown)
     */
    role: z.enum(['filing', 'former', 'substituting', 'associating']).optional(),
});
export const CaseSchema = z.object({
    id: z.string().uuid(),
    caseNumber: z.string(),
    title: z.string(),
    category: CaseCategorySchema,
    status: CaseStatusSchema,
    court: z.string(),
    courtLocation: z.string().optional(),
    jurisdiction: z.enum(['state', 'federal']),
    state: z.string().optional(),
    filingDate: z.string().datetime().optional(),
    parties: z.array(PartySchema).optional(),
    matter: z.object({
        id: z.string().uuid(),
        name: z.string(),
        number: z.string().optional(),
    }).optional(),
    notes: z.string().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    userId: z.string().uuid(),
    // eFiling-specific case fields
    caseType: CaseTypeRefSchema.optional(),
    caseSubType: CaseSubTypeRefSchema.optional(),
    jurisdictionalAmount: JurisdictionalAmountSchema.optional(),
    courtJurisdiction: CourtJurisdictionRefSchema.optional(),
    incidentZip: z.string().optional(),
    trialCourtCaseNumber: z.string().optional(),
    additionalInfo: AdditionalCaseInfoSchema.optional(),
    demandAmount: z.number().optional(),
    premisesAddress: PremisesAddressSchema.optional(),
    caseTypeMetadata: CaseTypeMetadataSchema.optional(),
    // eFiling parties (separate from legacy parties)
    efilingParties: z.array(EFilingPartySchema).optional(),
    efilingAttorneys: z.array(EFilingAttorneySchema).optional(),
});
export const CreateCaseSchema = CaseSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
});
export const UpdateCaseSchema = CaseSchema.partial().omit({
    id: true,
    createdAt: true,
    userId: true,
});
// ---------------------------------------------------------------------------
// Case portfolio view (DB-backed aggregation of orders by caseNumber)
// ---------------------------------------------------------------------------
/**
 * Derived status of a case, computed from its constituent orders' statuses.
 * Distinct from CaseStatusSchema (the legacy per-case status).
 */
export const CaseListStatusSchema = z.enum([
    'needs_attention',
    'pending',
    'active',
    'closed',
]);
/** A party's display name + role, extracted from an order's typeData JSONB. */
export const CasePartySchema = z.object({
    name: z.string(),
    role: z.string(),
});
export const CaseFilingTypeSchema = z.enum(['efiling', 'physical']);
export const CaseLastActivitySchema = z.object({
    date: z.string().datetime(),
    description: z.string(),
});
/** One row in the case portfolio list — an aggregate over orders sharing a caseNumber. */
export const CaseListItemSchema = z.object({
    caseNumber: z.string(),
    caseTitle: z.string().nullable(),
    caseCategory: z.string().nullable(),
    county: z.string().nullable(),
    courtLocation: z.string().nullable(),
    status: CaseListStatusSchema,
    lastActivity: CaseLastActivitySchema,
    parties: z.array(CasePartySchema),
    orderCount: z.number().int(),
    documentCount: z.number().int(),
    filingType: CaseFilingTypeSchema,
    createdAt: z.string().datetime(),
});
/** Dashboard-style aggregate stats over a user's case portfolio. */
export const CaseStatsSchema = z.object({
    total: z.number().int(),
    active: z.number().int(),
    needsAttention: z.number().int(),
    closedThisYear: z.number().int(),
    counties: z.number().int(),
});
/** Query params for GET /api/cases. */
export const CaseListQuerySchema = z.object({
    status: CaseListStatusSchema.optional(),
    county: z.string().optional(),
    search: z.string().optional(),
    recentlyFiled: z.coerce.boolean().optional(),
    sort: z.enum(['lastActivity', 'caseTitle', 'createdAt']).default('lastActivity'),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(20),
});
